import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  CAMPAIGN_TABLE_HEADERS,
  INITIAL_CAMPAIGN_FILTERS,
  CAMPAIGN_FILTER_STORAGE_KEY,
} from '../../constants/campaign'
import type {
  CampaignCorrectionPayloadType,
  CampaignCorrectionResultType,
  CampaignFilterOptionsType,
  CampaignFilterParamsType,
  CampaignHistoryRowType,
  CampaignPaginationType,
  CampaignRowType,
  CampaignWidgetSummaryType,
} from '../../@types/campaignTypes'
import type { SavedOptionsType } from '../../helper/forecastFilter'
import { loadFiltersFromStorage } from '../../helper/forecastFilter'
import {
  fetchScmsCampaignHistoryList,
  fetchScmsCampaignList,
  downloadCampaignCsv,
  submitCampaignCorrections,
} from './campaignThunk'

export type CampaignStateType = {
  columnVisibility: Record<string, boolean>
  selectedCampaignFilters: CampaignFilterParamsType
  appliedCampaignFilters: CampaignFilterParamsType
  savedSelectedOptions: SavedOptionsType | null
  rows: CampaignRowType[]
  pagination: CampaignPaginationType | null
  widgetSummary: CampaignWidgetSummaryType | null
  needPlanCount: number
  historyRows: CampaignHistoryRowType[]
  historyPagination: CampaignPaginationType | null
  correctionRows: CampaignCorrectionPayloadType[]
  correctionResult: CampaignCorrectionResultType | null
  isLoading: boolean
  isSubmittingCorrection: boolean
  error: string | null
  filterOptions: CampaignFilterOptionsType
}

export const sliceName = 'scmsCampaign'
const DEFAULT_ERROR_MESSAGE = 'An error occurred'
const SELECTION_PRESERVING_FILTERS = new Set<keyof CampaignFilterParamsType>([
  'cursor',
  'page_size',
  'sort_by',
  'sort_direction',
])

const hasSelectionResettingFilterChanged = (
  currentFilters: CampaignFilterParamsType,
  nextFilters: CampaignFilterParamsType,
) =>
  (Object.keys(nextFilters) as (keyof CampaignFilterParamsType)[]).some(
    (key) => !SELECTION_PRESERVING_FILTERS.has(key) && currentFilters[key] !== nextFilters[key],
  )

export const buildDefaultColumnVisibility = (): Record<string, boolean> =>
  Object.fromEntries(CAMPAIGN_TABLE_HEADERS.map((header) => [header.label, header.isShow]))

const { filters: storedFilters, savedOptions: storedSavedOptions } = loadFiltersFromStorage(
  CAMPAIGN_FILTER_STORAGE_KEY,
)
const storedCampaignFilters = storedFilters as Partial<CampaignFilterParamsType>
const hydratedFilters = Object.fromEntries(
  Object.entries(INITIAL_CAMPAIGN_FILTERS).map(([key, defaultValue]) => [
    key,
    key === 'final_fc_status'
      ? defaultValue
      : storedCampaignFilters[key as keyof CampaignFilterParamsType] ?? defaultValue,
  ]),
) as CampaignFilterParamsType

const initialState: CampaignStateType = {
  columnVisibility: buildDefaultColumnVisibility(),
  selectedCampaignFilters: hydratedFilters,
  appliedCampaignFilters: hydratedFilters,
  savedSelectedOptions: storedSavedOptions,
  rows: [],
  pagination: null,
  widgetSummary: null,
  needPlanCount: 0,
  historyRows: [],
  historyPagination: null,
  correctionRows: [],
  correctionResult: null,
  isLoading: false,
  isSubmittingCorrection: false,
  error: null,
  filterOptions: {
    brandOptions: [],
    categoryL1Options: [],
    categoryL2FlatOptions: [],
    picOptions: [],
    businessLineOptions: [],
    sourceOptions: [],
    productTypeOptions: [],
    paretoOptions: [],
  },
}

const campaignSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset: () => initialState,
    setColumnVisibility: (state, action: PayloadAction<{ label: string; visible: boolean }>) => {
      state.columnVisibility[action.payload.label] = action.payload.visible
    },
    setGroupVisibility: (state, action: PayloadAction<{ labels: string[]; visible: boolean }>) => {
      action.payload.labels.forEach((label) => {
        state.columnVisibility[label] = action.payload.visible
      })
    },
    setAllColumnVisibility: (state, action: PayloadAction<Record<string, boolean>>) => {
      state.columnVisibility = action.payload
    },
    setSelectedCampaignFilters: (
      state,
      action: PayloadAction<Partial<CampaignFilterParamsType>>,
    ) => {
      state.selectedCampaignFilters = { ...state.selectedCampaignFilters, ...action.payload }
    },
    setAppliedCampaignFilters: (state, action: PayloadAction<CampaignFilterParamsType>) => {
      if (hasSelectionResettingFilterChanged(state.appliedCampaignFilters, action.payload)) {
        state.correctionRows = []
        state.correctionResult = null
      }
      state.appliedCampaignFilters = action.payload
    },
    clearSelectedCampaignFilters: (state) => {
      state.selectedCampaignFilters = INITIAL_CAMPAIGN_FILTERS
      state.savedSelectedOptions = null
    },
    setCampaignSavedOptions: (state, action: PayloadAction<SavedOptionsType>) => {
      state.savedSelectedOptions = action.payload
    },
    setCampaignCorrectionRows: (state, action: PayloadAction<CampaignCorrectionPayloadType[]>) => {
      state.correctionRows = action.payload
    },
    clearCampaignCorrections: (state) => {
      state.correctionRows = []
      state.correctionResult = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchScmsCampaignList.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchScmsCampaignList.fulfilled, (state, action) => {
        state.isLoading = false
        state.rows = action.payload?.list?.data ?? []
        state.pagination = action.payload?.list?.pagination ?? null
        state.widgetSummary = action.payload?.widget?.data ?? null
        state.needPlanCount = action.payload?.needPlan?.data?.total_need_plans ?? 0
      })
      .addCase(fetchScmsCampaignList.rejected, (state, action) => {
        state.isLoading = false
        state.error = String(action.payload ?? action.error.message ?? DEFAULT_ERROR_MESSAGE)
      })
      .addCase(fetchScmsCampaignHistoryList.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchScmsCampaignHistoryList.fulfilled, (state, action) => {
        state.isLoading = false
        state.historyRows = action.payload?.data ?? []
        state.historyPagination = action.payload?.pagination ?? null
      })
      .addCase(fetchScmsCampaignHistoryList.rejected, (state, action) => {
        state.isLoading = false
        state.error = String(action.payload ?? action.error.message ?? DEFAULT_ERROR_MESSAGE)
      })
      .addCase(submitCampaignCorrections.pending, (state) => {
        state.isSubmittingCorrection = true
        state.error = null
      })
      .addCase(submitCampaignCorrections.fulfilled, (state, action) => {
        state.isSubmittingCorrection = false
        state.correctionResult = action.payload
      })
      .addCase(submitCampaignCorrections.rejected, (state, action) => {
        state.isSubmittingCorrection = false
        state.error = String(action.payload ?? action.error.message ?? DEFAULT_ERROR_MESSAGE)
      })
      .addCase(downloadCampaignCsv.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(downloadCampaignCsv.fulfilled, (state) => {
        state.isLoading = false
      })
      .addCase(downloadCampaignCsv.rejected, (state, action) => {
        state.isLoading = false
        state.error = String(action.payload ?? action.error.message ?? DEFAULT_ERROR_MESSAGE)
      })
  },
})

export const {
  reset,
  setColumnVisibility,
  setGroupVisibility,
  setAllColumnVisibility,
  setSelectedCampaignFilters,
  setAppliedCampaignFilters,
  clearSelectedCampaignFilters,
  setCampaignSavedOptions,
  setCampaignCorrectionRows,
  clearCampaignCorrections,
} = campaignSlice.actions

export default campaignSlice.reducer
